import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Brand } from '@app/models/brand.model';
import { VisitorType } from '@app/models/visitortype.model';
import { LocalizationService } from '../internationalization/localization.service';
import { FormControl } from '@angular/forms';
import { ReferenceService } from '@app/services/reference/reference.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { MatSort, Sort } from '@angular/material/sort';
import { VisitorTypeUploadReason } from '@app/models/visitortypeuploadreason.model';

@Component({
    selector: 'app-visitor-type-management',
    templateUrl: './visitor-type-management.component.html',
    styleUrls: ['./visitor-type-management.component.scss'],
    standalone: false
})
export class VisitorTypeManagementComponent implements OnInit, AfterViewInit {
  readonly VISITOR_TYPE: string = 'VisitorType';
  readonly UPLOAD_REASON: string = 'UploadReason';

  visitorTypes: VisitorType[] = [];
  visitorTypeUploadReasons: VisitorTypeUploadReason[] = [];
  brands: Brand[] = [];
  editToolTip: string;
  deleteToolTip: string;
  displayedVisitorTypeColumns: string[] = ['Description', 'Brand', 'Domain', 'IsActive', 'Actions'];
  displayedUploadReasonColumns: string[] = ['Description', 'IsActive', 'Actions'];
  searchControl: FormControl = new FormControl('');
  debounce: number = 1000;

  dataSource = new MatTableDataSource<VisitorType>(this.visitorTypes);
  @ViewChild(MatPaginator) paginator: MatPaginator = new MatPaginator(new MatPaginatorIntl(), ChangeDetectorRef.prototype);
  @ViewChild(MatSort) sort: MatSort;
  pageSize: number = 10;
  totalItems: number = 0;

  selectAllBrandsOption: Brand = { id: 'all', code: '', name: 'All', imageUrl: '', isActive: true, sites: [], };
  selectedBrand: Brand = this.selectAllBrandsOption;
  searchValue: string = '';

  selectedIsActive: boolean = true;
  selectedSearchValue: string = '';

  uploadReasonSearchControl: FormControl = new FormControl('');
  uploadReasonDataSource = new MatTableDataSource<VisitorTypeUploadReason>(this.visitorTypeUploadReasons);
  @ViewChild('uploadReasonPaginator') uploadReasonPaginator: MatPaginator = new MatPaginator(new MatPaginatorIntl(), ChangeDetectorRef.prototype);
  @ViewChild(MatSort) uploadReasonSort: MatSort;
  uploadReasonPageSize: number = 10;
  uploadReasonTotalItems: number = 0;
  selectedUploadReasonIsActive: boolean = true;
  selectedUploadReasonSearchValue: string = '';

  constructor(
    private _router: Router,
    private _localizationService: LocalizationService,
    private _referenceService: ReferenceService,
    private _addFileDialog: MatDialog,
    private _navigationService: NavigationService,
  ) {
    this._navigationService.onChange(true, false);
    this.editToolTip = this._localizationService.translate("visitor_type_management_visitor_type_edit_tooltip");
    this.deleteToolTip = this._localizationService.translate("visitor_type_management_visitor_type_delete_tooltip");
  }

  async ngOnInit(): Promise<void> {
    await this.loadVisitorTypes();
    this.closeSearch(this.VISITOR_TYPE);
    this.searchValues(this.VISITOR_TYPE);

    await this.loadUploadReasons();
    this.closeSearch(this.UPLOAD_REASON);
    this.searchValues(this.UPLOAD_REASON);
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.uploadReasonDataSource.paginator = this.uploadReasonPaginator;
    this.uploadReasonDataSource.sort = this.uploadReasonSort;
  }

  searchValues(type: string) {
    if(type === this.VISITOR_TYPE)
    {
      this.searchControl.valueChanges
      .pipe(debounceTime(this.debounce), distinctUntilChanged())
      .subscribe(query => {

        if(query && query.length > 2){
          this.selectedSearchValue = query.toLowerCase();
        }else{
          this.selectedSearchValue = '';
        }
        this.onFilterChange(this.VISITOR_TYPE);
      });

    }else if(type === this.UPLOAD_REASON){
      this.uploadReasonSearchControl.valueChanges
        .pipe(debounceTime(this.debounce), distinctUntilChanged())
        .subscribe(query => {

          if(query && query.length > 2){
            this.selectedUploadReasonSearchValue = query.toLowerCase();
          }else{
            this.selectedUploadReasonSearchValue = '';
          }
          this.onFilterChange(this.UPLOAD_REASON);
      });
    }
      
  }

  selectBrand(brandId: string) {
    this.selectedBrand = this.brands.find(b => b.id == brandId)!;
    this.onFilterChange(this.VISITOR_TYPE);
  }

  onFilterChange(type: string){

    if(type === this.UPLOAD_REASON){
      this.uploadReasonDataSource.data = this.visitorTypeUploadReasons.filter(vt =>
        vt.description.toLocaleLowerCase().includes(this.selectedUploadReasonSearchValue) &&
        vt.isActive == this.selectedUploadReasonIsActive
      );
    } else if (type === this.VISITOR_TYPE){
      this.dataSource.data = this.visitorTypes.filter(vt =>
        (vt.description.toLocaleLowerCase().includes(this.selectedSearchValue) ||
        (vt.domain && vt.domain.name && vt.domain.name.toLocaleLowerCase().includes(this.selectedSearchValue))) &&
        vt.isActive == this.selectedIsActive
      );

      if (this.selectedBrand && this.selectedBrand.id != this.selectAllBrandsOption.id) {
        this.dataSource.data = this.dataSource.data.filter(vt =>
          vt.brand!.id.toLocaleLowerCase().includes(this.selectedBrand.id)
        );
      }
    }
  }

  async loadVisitorTypes() {
    this._referenceService.getAllVisitorTypes(true).subscribe({
      next: (visitorTypes) => {
        if (visitorTypes !== null) {
          this.visitorTypes = visitorTypes;
          this.dataSource.data = visitorTypes;
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
          this.loadBrands(visitorTypes);
          this.onFilterChange(this.VISITOR_TYPE);
        }
      },
      error: (error) => {
        console.error(this._localizationService.translate("visitor_type_management_error_fetching_visitor_types"), error);
      }
    });
  }

  async loadUploadReasons() {
    this._referenceService.getGeneralUploadReasons(true).subscribe({
      next: (visitorTypeUploadReasons) => {
        if (visitorTypeUploadReasons !== null) {
          this.visitorTypeUploadReasons = visitorTypeUploadReasons;
          this.uploadReasonDataSource.data = visitorTypeUploadReasons;
          this.uploadReasonDataSource.paginator = this.uploadReasonPaginator;
          this.uploadReasonDataSource.sort = this.uploadReasonSort;
          this.onFilterChange(this.UPLOAD_REASON);
        }
      },
      error: (error) => {
        console.error(this._localizationService.translate("visitor_type_management_error_fetching_visitor_types"), error);
      }
    });
  }

  loadBrands(visitorTypes: VisitorType[]): void {
    const allBrands: Brand[] = visitorTypes
      .map(vt => vt.brand)
      .filter((brand): brand is Brand => !!brand);

    this.brands = allBrands.filter((brand, index, self) =>
      index === self.findIndex(b => b.name === brand.name)
    );

    this.brands.unshift(this.selectAllBrandsOption);
  }

  addNewVisitorType(): void {
    this._router.navigate(['/visitortypeaddeditmanagement']);
  }

  editVisitorType(visitorType: VisitorType): void {
    this._router.navigate(['/visitortypeaddeditmanagement', visitorType.id]);
  }

  addNewUploadReason(): void {
    this._router.navigate(['/uploadreasonsaddeditmanagement'], { queryParams: { id: '', visitortypeid: '' } });
  }

  editUploadReason(visitorTypeUploadReason: VisitorTypeUploadReason): void {
    this._router.navigate(['/uploadreasonsaddeditmanagement'], { queryParams: { id: visitorTypeUploadReason.id, visitortypeid: '' } });
  }

  onSortChange(event: any, type: string) {
    if (!event.active || event.direction === '') {
      return;
    }

    if(type === this.VISITOR_TYPE) {
      
      this.dataSource.data = this.dataSource.data.slice().sort((a, b) => {
        const isAsc = event.direction === 'asc';
          switch (event.active) {
            case 'Description': return this.compare(a.description || '', b.description || '', isAsc);
            case 'Brand': return this.compare(a.brand?.name || '', b.brand?.name || '', isAsc);
            case 'Domain': return this.compare(a.domain?.name || '', b.domain?.name || '', isAsc);
            case 'IsActive': return this.compare(a.isActive || '', b.isActive || '', isAsc);
            default: return 0;
        }
      });
    }else if(type === this.UPLOAD_REASON) {

      this.uploadReasonDataSource.data = this.uploadReasonDataSource.data.slice().sort((a, b) => {
        const isAsc = event.direction === 'asc';
        switch (event.active) {
          case 'Description': return this.compare(a.description || '', b.description || '', isAsc);
          case 'IsActive': return this.compare(a.isActive || '', b.isActive || '', isAsc);
          default: return 0;
        }
      });

    }
  }

  compare(a: string | number | boolean, b: string | number | boolean, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  closeVisitorTypeManagement(): void {
    this._router.navigate(['signin']);
  }

  openSearch(type: string) {
    if(type === this.UPLOAD_REASON) {
      this.uploadReasonSearchControl.setValue('');
    } else if(type === this.VISITOR_TYPE){
      this.searchControl.setValue('');
    }
  };

  closeSearch(type: string) {
    if(type === this.UPLOAD_REASON) {
      this.uploadReasonSearchControl.setValue(null);
      this.selectedUploadReasonSearchValue = '';
    } else if(type === this.VISITOR_TYPE){ 
      this.searchControl.setValue(null);
      this.searchValue = '';
      this.selectBrand(this.selectAllBrandsOption.id);
    }

  };

  showSearchButton(type: string) {
    if(type === this.UPLOAD_REASON) {
      return this.uploadReasonSearchControl.value === null;
    }else if(type === this.VISITOR_TYPE){ 
      return this.searchControl.value === null;
    }

    return false;
  };

  showSearchInput(type: string) {
    if(type === this.UPLOAD_REASON) {
      return this.uploadReasonSearchControl.value !== null;
    } else if(type === this.VISITOR_TYPE){ 
      return this.searchControl.value !== null;
    }

    return false;
  };

  onSearchInputKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

}